import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Moon, Sun, X } from 'lucide-react';
import { Logo } from '@/brand/Logo';
import { useI18n } from '@/lib/i18n';
import { useTheme } from '@/lib/theme';
import { useHydrated } from '@/lib/hydration';
import { ButtonLink, IconButton } from '@/ui';
import { trackClick, trackView, trackVisit } from './metrics';
import './site.css';

/** Header, footer and the language / theme switches shared by the public pages. */

export const COMPANY = {
  name: 'Hanseatic LifeScience Consulting',
  street: 'Grenzweg 45',
  city: '21244 Buchholz',
  country: 'Germany',
  email: 'info@h-lsc.com',
  web: 'www.h-lsc.com',
  directors: 'René Wittwer, Nils Jonescheit',
  vat: 'DE363035731',
};

function useLangPrefix() {
  const { lang } = useI18n();
  return lang === 'de' ? '/de' : '';
}

export function SiteHeader() {
  const { t, lang, setLang } = useI18n();
  const { theme: storedTheme, toggle } = useTheme();
  // The page was prerendered in light. Until it has hydrated, render the
  // switch as the server did; theme-init.js has already set the colours.
  const hydrated = useHydrated();
  const theme = hydrated ? storedTheme : 'light';
  const prefix = useLangPrefix();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: `${prefix}/#how`, label: t.site.nav.how },
    { to: `${prefix}/security`, label: t.site.nav.security },
    { to: `${prefix}/contact`, label: t.site.nav.contact },
  ];

  return (
    <header className="site-header">
      <div className="site-shell site-header-inner">
        <Link to={prefix || '/'} className="site-logo" aria-label={t.meta.product}>
          {/* Keyed on the route so the reveal replays on every navigation,
              not just the first time this header ever mounts — otherwise a
              visitor who arrives lower in the app never sees it play at
              all. */}
          <Logo key={location.pathname} size="md" markSize={34} wave />
        </Link>

        <nav className={open ? 'site-nav is-open' : 'site-nav'} aria-label={t.site.nav.product}>
          {links.map((link) => (
            <a key={link.to} href={link.to} className="site-nav-link">
              {link.label}
            </a>
          ))}
          <div className="site-nav-actions">
            <div className="lang-switch" role="group" aria-label={t.common.language}>
              <button type="button" className={lang === 'en' ? 'is-active' : ''} onClick={() => setLang('en')}>
                EN
              </button>
              <button type="button" className={lang === 'de' ? 'is-active' : ''} onClick={() => setLang('de')}>
                DE
              </button>
            </div>
            <IconButton label={theme === 'dark' ? t.common.themeLight : t.common.themeDark} onClick={toggle}>
              {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
            </IconButton>
            <ButtonLink to="/app/signin" variant="ghost" size="sm">
              {t.site.nav.signIn}
            </ButtonLink>
            <ButtonLink to="/contact" variant="primary" size="sm" onClick={() => trackClick('nav_start', location.pathname)}>
              {t.site.nav.start}
            </ButtonLink>
          </div>
        </nav>

        <IconButton label={open ? t.common.close : t.nav.commands} className="site-burger" onClick={() => setOpen((v) => !v)}>
          {open ? <X size={20} /> : <Menu size={20} />}
        </IconButton>
      </div>
    </header>
  );
}

export function SiteFooter() {
  const { t } = useI18n();
  const prefix = useLangPrefix();
  const year = new Date().getFullYear();

  return (
    <footer className="site-footer">
      <div className="site-shell site-footer-inner">
        <div className="site-footer-brand">
          <Logo size="md" />
        </div>

        <div className="site-footer-links">
          <div>
            <p className="eyebrow">{t.site.footer.product}</p>
            <a href={`${prefix}/#how`}>{t.site.nav.how}</a>
            <Link to={`${prefix}/security`}>{t.site.nav.security}</Link>
            <Link to="/contact">{t.site.nav.start}</Link>
          </div>
          <div>
            <p className="eyebrow">{t.site.footer.company}</p>
            <a href={`https://${COMPANY.web}`} target="_blank" rel="noreferrer noopener">
              {COMPANY.web}
            </a>
            <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
            <Link to={`${prefix}/contact`}>{t.site.nav.contact}</Link>
          </div>
          <div>
            <p className="eyebrow">{t.site.footer.legal}</p>
            <Link to={`${prefix}/imprint`}>{t.site.legal.imprint}</Link>
            <Link to={`${prefix}/privacy`}>{t.site.legal.privacy}</Link>
          </div>
        </div>
      </div>
      <div className="site-shell site-footer-base">
        <span className="subtle">
          © {year} {COMPANY.name}. {t.site.footer.rights}
        </span>
        <span className="subtle">
          {COMPANY.street} · {COMPANY.city}
        </span>
      </div>
    </footer>
  );
}

export function SitePage({ children, className }: { children: ReactNode; className?: string }) {
  const location = useLocation();

  // The marketing beacon (metrics.ts) — every public page renders through
  // this one wrapper, so this is the single place that needs to know about
  // it rather than every page under web/src/site/ individually. The visit
  // call is per-tab-gated inside trackVisit itself, so firing it on every
  // navigation (not just the first) is harmless — it only ever actually
  // sends once.
  useEffect(() => {
    trackVisit(location.pathname);
    trackView(location.pathname);
  }, [location.pathname]);

  return (
    <div className={['site', className || ''].filter(Boolean).join(' ')}>
      {/* The continuously-animating lattice field was removed on 28 Sep 2026:
          the owner asked for motion only in response to scrolling. Everything
          that still moves does so through <Reveal> (reveal.tsx), which is an
          IntersectionObserver firing once per element as it enters the
          viewport. LatticeField.tsx is kept in the tree — it is the approved
          "Exploded View" art direction and may be wanted again — but nothing
          mounts it. */}
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <SiteHeader />
      <main id="main">{children}</main>
      <SiteFooter />
    </div>
  );
}
